import React, { useCallback, useEffect, useState } from "react";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

// ── Constants ─────────────────────────────────────────────────────────────────
const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };

const PRIORITY_CONFIG = {
  urgent: { label: "Urgent", color: "#991B1B", bg: "#FEF2F2", dot: "#DC2626" },
  high: { label: "High", color: "#92400E", bg: "#FFFBEB", dot: "#D97706" },
  medium: { label: "Medium", color: "#1E40AF", bg: "#EFF6FF", dot: "#3B82F6" },
  low: { label: "Low", color: "#4B5563", bg: "#F9FAFB", dot: "#9CA3AF" },
};

const FILTERS = [
  { key: "open", label: "Open" },
  { key: "closed", label: "Done" },
  { key: "all", label: "All" },
];

const pCfg = (p) => PRIORITY_CONFIG[p] || PRIORITY_CONFIG.medium;

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
  },
  spinner: {
    display: "inline-block",
    width: 14,
    height: 14,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 5,
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    overflow: "hidden",
    marginBottom: "0.625rem",
    transition: "box-shadow 0.15s ease",
  },
  btn: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  btnPrimary: {
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 16px",
    borderRadius: 8,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    cursor: "pointer",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  btnDanger: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: 6,
    border: "1px solid #FECACA",
    background: "transparent",
    color: "#991B1B",
    cursor: "pointer",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  btnGhost: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: 6,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  pill: (variant) => {
    const map = {
      green: { background: "#F0FDF4", color: "#166534" },
      amber: { background: "#FFFBEB", color: "#92400E" },
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      red: { background: "#FEF2F2", color: "#991B1B" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
    };
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      ...(map[variant] || map.gray),
    };
  },
  kpiCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "0.75rem 1rem",
    textAlign: "center",
    minWidth: 72,
  },
  // Modal overlay
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1060,
    padding: "1rem",
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    width: "100%",
    maxWidth: 520,
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
  },
  modalLg: {
    background: "#fff",
    borderRadius: 14,
    width: "100%",
    maxWidth: 680,
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
  },
  modalHead: {
    padding: "1.125rem 1.25rem 0.875rem",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalBody: { padding: "1.125rem 1.25rem" },
  modalFoot: {
    padding: "0.875rem 1.25rem",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    flexWrap: "wrap",
  },
  formGroup: { marginBottom: "1rem" },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    display: "block",
    marginBottom: 5,
  },
  input: {
    fontSize: 13,
    padding: "7px 11px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    color: "#1a1d23",
    background: "#fff",
    width: "100%",
    outline: "none",
    fontFamily: "inherit",
  },
  textarea: {
    fontSize: 13,
    padding: "7px 11px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    color: "#1a1d23",
    background: "#fff",
    width: "100%",
    outline: "none",
    resize: "none",
    fontFamily: "inherit",
  },
  select: {
    fontSize: 13,
    padding: "7px 11px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    color: "#1a1d23",
    background: "#fff",
    width: "100%",
    outline: "none",
    fontFamily: "inherit",
  },
  noteBox: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "8px 10px",
    marginTop: 8,
    whiteSpace: "pre-wrap",
  },
  infoBanner: {
    background: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: 8,
    padding: "0.625rem 0.875rem",
    fontSize: 12,
    color: "#92400E",
    marginBottom: "1rem",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  errorBanner: {
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "0.625rem 0.875rem",
    fontSize: 12,
    color: "#991B1B",
    marginBottom: "1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseDateOnly = (iso) => {
  if (!iso) return null;
  const str = typeof iso === "string" ? iso : new Date(iso).toISOString();
  const [y, m, d] = str.slice(0, 10).split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDue = (iso) => {
  const d = parseDateOnly(iso);
  if (!d) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const isOverdue = (task) => {
  if (task.status !== "open" || !task.dueDate) return false;
  const end = parseDateOnly(task.dueDate);
  if (!end) return false;
  end.setHours(23, 59, 59, 999);
  return end < new Date();
};

// ── Sub-components ────────────────────────────────────────────────────────────
const Spinner = () => (
  <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <span style={s.spinner} />
  </>
);

const Pill = ({ variant, children }) => <span style={s.pill(variant)}>{children}</span>;

const PlusIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const EditIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const CalIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginRight: 3, verticalAlign: "middle" }}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// ── Checkbox ──────────────────────────────────────────────────────────────────
const Checkbox = ({ checked, onChange, disabled }) => (
  <div
    onClick={disabled ? undefined : onChange}
    style={{
      width: 20,
      height: 20,
      borderRadius: 6,
      border: checked ? "2px solid #166534" : "2px solid #d1d5db",
      background: checked ? "#166534" : "#fff",
      cursor: disabled ? "default" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      transition: "all 0.15s",
    }}
  >
    {checked && <CheckIcon />}
  </div>
);

// ── Modal wrapper ─────────────────────────────────────────────────────────────
const Modal = ({ show, onClose, title, large, children, footer }) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    if (show) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [show, onClose]);

  if (!show) return null;
  return (
    <div style={s.overlay} onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div style={large ? s.modalLg : s.modal}>
        <div style={s.modalHead}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#6b7280",
              display: "flex",
            }}
          >
            <CloseIcon />
          </button>
        </div>
        <div style={s.modalBody}>{children}</div>
        {footer && <div style={s.modalFoot}>{footer}</div>}
      </div>
    </div>
  );
};

// ── Form field ────────────────────────────────────────────────────────────────
const Field = ({ label, hint, children }) => (
  <div style={s.formGroup}>
    <label style={s.label}>
      {label}
      {hint && <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 4 }}>{hint}</span>}
    </label>
    {children}
  </div>
);

const EMPTY_CREATE = { title: "", description: "", priority: "medium", dueDate: "" };
const EMPTY_EDIT = {
  title: "",
  description: "",
  priority: "medium",
  dueDate: "",
  notes: "",
  status: "open",
};

// ── Main component ────────────────────────────────────────────────────────────
function PropertyMaintenance({ property, readOnly = false }) {
  const { getAuthHeaders } = useAuth();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("open");

  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);

  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);

  const propertyId = property?._id;

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchTasks = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(API_ENDPOINTS.maintenanceTasks.list(propertyId, filter), {
        headers: { "ngrok-skip-browser-warning": "true", ...getAuthHeaders() },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Could not load maintenance tasks");
      const list = data.data || [];
      list.sort((a, b) => {
        if (a.status !== b.status) return a.status === "open" ? -1 : 1;
        const pd = (PRIORITY_ORDER[a.priority] ?? 2) - (PRIORITY_ORDER[b.priority] ?? 2);
        if (pd !== 0) return pd;
        const ad = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bd = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        if (ad !== bd) return ad - bd;
        return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      });
      setTasks(list);
    } catch (e) {
      setError(e.message || "Failed to load tasks");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [propertyId, filter, getAuthHeaders]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    const title = createForm.title.trim();
    if (!title || !propertyId || readOnly) return;
    setCreateSubmitting(true);
    try {
      const res = await fetch(API_ENDPOINTS.maintenanceTasks.create(propertyId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          title,
          description: createForm.description.trim() || undefined,
          priority: createForm.priority,
          dueDate: createForm.dueDate || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = Array.isArray(data?.errors)
          ? data.errors.map((x) => x.msg).join(", ")
          : data?.message;
        throw new Error(msg || "Could not create task");
      }
      setCreateForm(EMPTY_CREATE);
      setCreateOpen(false);
      await fetchTasks();
    } catch (err) {
      setError(err.message || "Create failed");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const openEdit = (task) => {
    setEditTask(task);
    setEditForm({
      title: task.title || "",
      description: task.description || "",
      priority: task.priority || "medium",
      dueDate: task.dueDate ? task.dueDate.slice(0, 10) : "",
      notes: task.notes || "",
      status: task.status || "open",
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editTask?._id || readOnly) return;
    setEditSubmitting(true);
    try {
      const res = await fetch(API_ENDPOINTS.maintenanceTasks.update(propertyId, editTask._id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          title: editForm.title.trim(),
          description: editForm.description.trim(),
          priority: editForm.priority,
          dueDate: editForm.dueDate || null,
          notes: editForm.notes.trim(),
          status: editForm.status,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = Array.isArray(data?.errors)
          ? data.errors.map((x) => x.msg).join(", ")
          : data?.message;
        throw new Error(msg || "Could not update task");
      }
      setEditOpen(false);
      setEditTask(null);
      await fetchTasks();
    } catch (err) {
      setError(err.message || "Update failed");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (task) => {
    if (!task?._id || readOnly) return;
    if (!window.confirm("Remove this maintenance task? This cannot be undone.")) return;
    try {
      const res = await fetch(API_ENDPOINTS.maintenanceTasks.delete(propertyId, task._id), {
        method: "DELETE",
        headers: { "ngrok-skip-browser-warning": "true", ...getAuthHeaders() },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Delete failed");
      if (editTask?._id === task._id) {
        setEditOpen(false);
        setEditTask(null);
      }
      await fetchTasks();
    } catch (err) {
      setError(err.message || "Delete failed");
    }
  };

  const handleQuickClose = async (task) => {
    if (!task?._id || readOnly) return;
    try {
      const res = await fetch(API_ENDPOINTS.maintenanceTasks.update(propertyId, task._id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ status: task.status === "closed" ? "open" : "closed" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Could not update task");
      await fetchTasks();
    } catch (err) {
      setError(err.message || "Update failed");
    }
  };

  if (!property) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", fontSize: 14, color: "#6b7280" }}>
        Property data not available
      </div>
    );
  }

  // ── Derived counts ────────────────────────────────────────────────────────
  const openCount = tasks.filter((t) => t.status === "open").length;
  const overdueCount = tasks.filter((t) => isOverdue(t)).length;
  const doneCount = tasks.filter((t) => t.status === "closed").length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: "1.25rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
            marginBottom: "1rem",
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>Maintenance</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Track tasks, priorities and deadlines for this property
            </div>
          </div>
          {!readOnly && (
            <div style={{ flexShrink: 0 }}>
              <button style={s.btnPrimary} onClick={() => setCreateOpen(true)}>
                <PlusIcon /> Add task
              </button>
            </div>
          )}
        </div>

        {/* KPI strip */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: "1rem" }}>
          {[
            { count: openCount, label: "Open", color: "#1E40AF" },
            { count: overdueCount, label: "Overdue", color: "#991B1B" },
            { count: doneCount, label: "Done", color: "#166534" },
          ].map(({ count, label, color }) => (
            <div key={label} style={s.kpiCard}>
              <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1 }}>{count}</div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: "#9ca3af",
                  marginTop: 3,
                }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Segmented filter */}
        <div
          style={{
            display: "flex",
            background: "#f3f4f6",
            border: "1px solid #e5e7eb",
            borderRadius: 7,
            padding: 2,
            gap: 2,
            width: "fit-content",
          }}
        >
          {FILTERS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                fontSize: 11,
                fontWeight: filter === key ? 600 : 400,
                padding: "4px 14px",
                borderRadius: 5,
                border: filter === key ? "1px solid #e5e7eb" : "1px solid transparent",
                background: filter === key ? "#fff" : "transparent",
                color: filter === key ? "#1a1d23" : "#6b7280",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Banners ── */}
      {readOnly && (
        <div style={s.infoBanner}>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Removed listing — tasks are read-only.
        </div>
      )}

      {error && (
        <div style={s.errorBanner}>
          <span>{error}</span>
          <button
            onClick={() => setError("")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#991B1B",
              display: "flex",
            }}
          >
            <CloseIcon />
          </button>
        </div>
      )}

      {/* ── Task list ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem 0", color: "#6b7280", fontSize: 13 }}>
          <Spinner /> Loading tasks…
        </div>
      ) : tasks.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            background: "#f9fafb",
            border: "2px dashed #e5e7eb",
            borderRadius: 12,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            {filter === "closed" ? "No completed tasks yet" : "No tasks here"}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#9ca3af",
              marginBottom: filter === "open" && !readOnly ? "1rem" : 0,
            }}
          >
            {filter === "open" ? "Add a task to track maintenance work." : ""}
          </div>
          {!readOnly && filter === "open" && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ flexShrink: 0 }}>
                <button style={s.btnPrimary} onClick={() => setCreateOpen(true)}>
                  <PlusIcon /> Add first task
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          {tasks.map((task) => {
            const overdue = isOverdue(task);
            const cfg = pCfg(task.priority);
            const isDone = task.status === "closed";

            return (
              <div
                key={task._id}
                style={{
                  ...s.card,
                  opacity: isDone ? 0.75 : 1,
                  borderLeft: `3px solid ${cfg.dot}`,
                }}
              >
                {/* Priority accent bar */}
                <div style={{ height: 2, background: cfg.dot }} />

                <div style={{ padding: "0.875rem 1rem" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    {/* Checkbox */}
                    {!readOnly && (
                      <div style={{ paddingTop: 2 }}>
                        <Checkbox checked={isDone} onChange={() => handleQuickClose(task)} />
                      </div>
                    )}

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Title + badges */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 6,
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: isDone ? "#9ca3af" : "#1a1d23",
                            textDecoration: isDone ? "line-through" : "none",
                          }}
                        >
                          {task.title}
                        </span>
                        <span
                          style={{
                            ...s.pill(
                              task.priority === "urgent"
                                ? "red"
                                : task.priority === "high"
                                  ? "amber"
                                  : task.priority === "medium"
                                    ? "blue"
                                    : "gray"
                            ),
                            fontSize: 10,
                          }}
                        >
                          {cfg.label}
                        </span>
                        {isDone && <span style={{ ...s.pill("green"), fontSize: 10 }}>Done</span>}
                        {overdue && <span style={{ ...s.pill("red"), fontSize: 10 }}>Overdue</span>}
                      </div>

                      {/* Description */}
                      {task.description && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginBottom: 6,
                            lineHeight: 1.5,
                          }}
                        >
                          {task.description}
                        </div>
                      )}

                      {/* Due + closed */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12,
                          fontSize: 11,
                          color: "#9ca3af",
                        }}
                      >
                        {task.dueDate ? (
                          <span style={{ color: overdue ? "#991B1B" : "#9ca3af" }}>
                            <CalIcon />
                            Due {formatDue(task.dueDate)}
                          </span>
                        ) : (
                          <span>
                            <CalIcon />
                            No due date
                          </span>
                        )}
                        {task.closedAt && (
                          <span style={{ color: "#166534" }}>
                            ✓ Closed {formatDue(task.closedAt)}
                          </span>
                        )}
                      </div>

                      {/* Notes */}
                      {task.notes && (
                        <div style={s.noteBox}>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              letterSpacing: "0.07em",
                              textTransform: "uppercase",
                              color: "#9ca3af",
                              marginBottom: 4,
                            }}
                          >
                            Notes
                          </div>
                          <div style={{ fontSize: 12, color: "#374151" }}>{task.notes}</div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      <button
                        style={s.btnGhost}
                        onClick={() => openEdit(task)}
                        title={readOnly ? "View" : "Edit"}
                      >
                        <EditIcon />
                        <span style={{ fontSize: 11 }}>{readOnly ? "View" : "Edit"}</span>
                      </button>
                      {!readOnly && (
                        <button
                          style={{ ...s.btnGhost, borderColor: "#FECACA", color: "#991B1B" }}
                          onClick={() => handleDelete(task)}
                          title="Delete"
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Create modal ── */}
      <Modal
        show={createOpen}
        onClose={() => !createSubmitting && setCreateOpen(false)}
        title="New maintenance task"
        footer={
          <>
            <div />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                style={s.btn}
                onClick={() => setCreateOpen(false)}
                disabled={createSubmitting}
              >
                Cancel
              </button>
              <button style={s.btnPrimary} onClick={handleCreate} disabled={createSubmitting}>
                {createSubmitting ? (
                  <>
                    <Spinner />
                    Adding…
                  </>
                ) : (
                  <>
                    <PlusIcon />
                    Add task
                  </>
                )}
              </button>
            </div>
          </>
        }
      >
        <form onSubmit={handleCreate}>
          <Field label="Title">
            <input
              style={s.input}
              required
              maxLength={200}
              placeholder="e.g. Service AC units, Fix gate latch"
              value={createForm.title}
              disabled={createSubmitting}
              onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
            />
          </Field>
          <Field label="Details" hint="(optional)">
            <textarea
              style={s.textarea}
              rows={3}
              placeholder="Additional context or instructions…"
              value={createForm.description}
              disabled={createSubmitting}
              onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>
          <div className="re-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.875rem" }}>
            <Field label="Priority">
              <select
                style={s.select}
                value={createForm.priority}
                disabled={createSubmitting}
                onChange={(e) => setCreateForm((f) => ({ ...f, priority: e.target.value }))}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </Field>
            <Field label="Due date" hint="(optional)">
              <input
                style={s.input}
                type="date"
                value={createForm.dueDate}
                disabled={createSubmitting}
                onChange={(e) => setCreateForm((f) => ({ ...f, dueDate: e.target.value }))}
              />
            </Field>
          </div>
        </form>
      </Modal>

      {/* ── Edit modal ── */}
      <Modal
        show={editOpen}
        onClose={() => !editSubmitting && setEditOpen(false)}
        title={readOnly ? "Task details" : "Edit task"}
        large
        footer={
          <>
            <div>
              {editTask && !readOnly && (
                <button
                  style={s.btnDanger}
                  onClick={() => handleDelete(editTask)}
                  disabled={editSubmitting}
                >
                  <TrashIcon /> Delete task
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={s.btn} onClick={() => setEditOpen(false)} disabled={editSubmitting}>
                {readOnly ? "Close" : "Cancel"}
              </button>
              {!readOnly && (
                <button style={s.btnPrimary} onClick={handleSaveEdit} disabled={editSubmitting}>
                  {editSubmitting ? (
                    <>
                      <Spinner />
                      Saving…
                    </>
                  ) : (
                    "Save changes"
                  )}
                </button>
              )}
            </div>
          </>
        }
      >
        <form onSubmit={handleSaveEdit}>
          <div className="re-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.875rem" }}>
            <Field label="Title">
              <input
                style={s.input}
                required
                readOnly={readOnly}
                value={editForm.title}
                disabled={editSubmitting}
                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              />
            </Field>
            <Field label="Priority">
              <select
                style={{ ...s.select, width: 120 }}
                disabled={readOnly || editSubmitting}
                value={editForm.priority}
                onChange={(e) => setEditForm((f) => ({ ...f, priority: e.target.value }))}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </Field>
          </div>
          <Field label="Details">
            <textarea
              style={s.textarea}
              rows={2}
              readOnly={readOnly}
              placeholder="Additional context or instructions…"
              value={editForm.description}
              disabled={editSubmitting}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>
          <div className="re-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.875rem" }}>
            <Field label="Due date">
              <input
                style={s.input}
                type="date"
                readOnly={readOnly}
                value={editForm.dueDate}
                disabled={editSubmitting}
                onChange={(e) => setEditForm((f) => ({ ...f, dueDate: e.target.value }))}
              />
              {!readOnly && (
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                  Clear the date to remove the deadline.
                </div>
              )}
            </Field>
            <Field label="Status">
              <select
                style={s.select}
                disabled={readOnly || editSubmitting}
                value={editForm.status}
                onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="open">Open</option>
                <option value="closed">Done</option>
              </select>
            </Field>
          </div>
          <Field label="Notes">
            <textarea
              style={s.textarea}
              rows={4}
              readOnly={readOnly}
              placeholder="Add visit notes, vendor updates, or what was done…"
              value={editForm.notes}
              disabled={editSubmitting}
              onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Field>
        </form>
      </Modal>
    </div>
  );
}

export default PropertyMaintenance;
